import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import axios from 'axios';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FilterProvider } from './contexts/FilterContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AIAssistant, AssistantProvider } from './components/AIAssistant';

// ─── Pages — code-split via React.lazy ─────────────────────────────
// Each page becomes its own JS chunk that the browser only fetches
// when the user actually navigates to that route. The login page
// (defined further down in this file as LoginRoute) stays in the
// main bundle so it shows immediately on first paint.
//
// Heavyweight pages (dashboards using recharts/leaflet) drop out of
// the login-page download path, so an unauthenticated visitor only
// pays for ~250 kB of code instead of the full 1.5 MB bundle.
// ────────────────────────────────────────────────────────────────────
const DrivesPage              = lazy(() => import('./pages/cell/DrivesPage').then(m => ({ default: m.DrivesPage })));
const DriveDetailPage         = lazy(() => import('./pages/cell/DriveDetailPage').then(m => ({ default: m.DriveDetailPage })));
const StudentRegistrationPage = lazy(() => import('./pages/cell/StudentRegistrationPage').then(m => ({ default: m.StudentRegistrationPage })));
const PublicJDPage            = lazy(() => import('./pages/cell/PublicJDPage').then(m => ({ default: m.PublicJDPage })));
const AdminOverviewPage       = lazy(() => import('./pages/cell/AdminOverviewPage').then(m => ({ default: m.AdminOverviewPage })));
const BulkMarkPage            = lazy(() => import('./pages/cell/BulkMarkPage').then(m => ({ default: m.BulkMarkPage })));
const ConsentScreen           = lazy(() => import('./pages/student/ConsentScreen').then(m => ({ default: m.ConsentScreen })));
const ProfileCompletionScreen = lazy(() => import('./pages/student/ProfileCompletionScreen').then(m => ({ default: m.ProfileCompletionScreen })));
const StudentDashboard        = lazy(() => import('./pages/StudentDashboard').then(m => ({ default: m.StudentDashboard })));
const HODDashboard            = lazy(() => import('./pages/HODDashboard').then(m => ({ default: m.HODDashboard })));
const DashboardShell          = lazy(() => import('./layouts/DashboardShell').then(m => ({ default: m.DashboardShell })));
const MyInternships           = lazy(() => import('./pages/student/MyInternships').then(m => ({ default: m.MyInternships })));
const AvailableDrives         = lazy(() => import('./pages/student/AvailableDrives').then(m => ({ default: m.AvailableDrives })));
const Achievements            = lazy(() => import('./pages/student/Achievements').then(m => ({ default: m.Achievements })));
const Freelancing             = lazy(() => import('./pages/student/Freelancing').then(m => ({ default: m.Freelancing })));
const Hackathons              = lazy(() => import('./pages/student/Hackathons').then(m => ({ default: m.Hackathons })));
const OtherEvents              = lazy(() => import('./pages/student/OtherEvents').then(m => ({ default: m.OtherEvents })));
const Documents               = lazy(() => import('./pages/student/Documents').then(m => ({ default: m.Documents })));
const Announcements           = lazy(() => import('./pages/student/Announcements').then(m => ({ default: m.Announcements })));
const ActivityLogPage         = lazy(() => import('./pages/student/ActivityLog').then(m => ({ default: m.ActivityLogPage })));
const ApprovalsPage           = lazy(() => import('./pages/common/ApprovalsPage').then(m => ({ default: m.ApprovalsPage })));
const ApprovalHistoryPage     = lazy(() => import('./pages/common/ApprovalHistoryPage').then(m => ({ default: m.ApprovalHistoryPage })));
const HODNotesPage            = lazy(() => import('./pages/common/HODNotesPage').then(m => ({ default: m.HODNotesPage })));
const SettingsPage            = lazy(() => import('./pages/common/SettingsPage').then(m => ({ default: m.SettingsPage })));
const UserManagement          = lazy(() => import('./pages/admin/UserManagement').then(m => ({ default: m.UserManagement })));
const StudentDetail           = lazy(() => import('./pages/admin/StudentDetail').then(m => ({ default: m.StudentDetail })));
const ImportsPage             = lazy(() => import('./pages/admin/ImportsPage').then(m => ({ default: m.ImportsPage })));
const AnnouncementsPage       = lazy(() => import('./pages/admin/AnnouncementsPage').then(m => ({ default: m.AnnouncementsPage })));
const HODAllocation           = lazy(() => import('./pages/admin/HODAllocation').then(m => ({ default: m.HODAllocation })));
const CohortInsights          = lazy(() => import('./pages/admin/CohortInsights').then(m => ({ default: m.CohortInsights })));
const HODCohortInsights       = lazy(() => import('./pages/hod/CohortInsights').then(m => ({ default: m.HODCohortInsights })));

// A simple loading spinner
const ScreenLoader = () => (
  <div className="flex h-screen w-screen items-center justify-center bg-surface-50 dark:bg-surface-950">
    <div className="h-10 w-10 animate-spin rounded-full border-4 border-surface-200 border-t-brand-500"></div>
  </div>
);

// Protected Route Wrapper intelligently selecting Dashboard by Role
const ProtectedDashboard = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <ScreenLoader />;
  if (!isAuthenticated || !user) return <Navigate to="/login" replace />;

  switch (user.role) {
    // Routed through ProtectedStudent (not just <StudentDashboard /> directly)
    // so the consent → profile-completion gate fires here too — /dashboard is
    // the default post-login destination, so without this a student could
    // land on real dashboard data having never been asked for consent.
    case 'STUDENT':      return <ProtectedStudent><StudentDashboard /></ProtectedStudent>;
    case 'HOD':          return <HODDashboard />;
    case 'ADMIN':        return <DashboardShell />;
    case 'COORDINATOR':  return <Navigate to="/cell/drives" replace />;
    case 'ASSISTANT':    return <Navigate to="/cell/drives" replace />;
    default:             return <Navigate to="/login" replace />;
  }
};

// Student-only guard: self-service pages redirect non-students to /dashboard
// (which then routes them to their role-appropriate landing page).
// Also gates on data_consent — shows ConsentScreen until the student agrees.
// Preserves the current path in state.from so LoginRoute can bounce back here
// after sign-in (e.g. a student clicks a JD link → logs in → lands on drives).
// Locked fields the registrar controls. Shown read-only in
// ProfileCompletionScreen so the student can confirm them before filling
// in the rest.
interface MeLocked {
  enrollment_number: string;
  full_name:         string;
  branch:            string;
  email:             string;
  graduation_year:   number | null;
}

type ContactPrefill = { personal_email?: string; linkedin_url?: string };

const ProtectedStudent = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const [checked,             setChecked]             = useState(false);
  const [hasConsent,          setHasConsent]          = useState(false);
  const [profileCompleted,    setProfileCompleted]    = useState(false);
  // Separate from profileCompleted: 78% of students finished their profile
  // before personal email / LinkedIn existed as fields, so they must be able
  // to re-enter the same screen for those two alone.
  const [contactCompleted,    setContactCompleted]    = useState(false);
  const [prefill,             setPrefill]             = useState<ContactPrefill>({});
  const [locked,              setLocked]              = useState<MeLocked | null>(null);

  // Fetch /students/me/ once and pull out: consent flag, profile_completed_at
  // flag, and the locked fields the completion screen needs to display.
  const fetchMe = useCallback(async () => {
    try {
      const res = await axios.get('/api/v1/portal/students/me/', {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });
      setHasConsent(res.data.data_consent === true);
      setProfileCompleted(res.data.profile_completed_at != null);
      setContactCompleted(res.data.contact_details_completed_at != null);
      setPrefill({
        personal_email: res.data.personal_email || '',
        linkedin_url:   res.data.linkedin_url   || '',
      });
      setLocked({
        enrollment_number: res.data.enrollment_number,
        full_name:         res.data.full_name,
        branch:            res.data.branch,
        email:             res.data.email,
        graduation_year:   res.data.graduation_year ?? null,
      });
    } catch {
      // Treat any fetch failure as "not yet onboarded". The guards below
      // will route to consent / profile completion, which themselves call
      // their own endpoints and will surface a clearer error if /me/ is
      // genuinely broken.
      setHasConsent(false);
      setProfileCompleted(false);
      setContactCompleted(false);
    } finally {
      setChecked(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'STUDENT') {
      fetchMe();
    }
  }, [isAuthenticated, user, fetchMe]);

  if (isLoading) return <ScreenLoader />;
  if (!isAuthenticated || !user)
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  if (user.role !== 'STUDENT') return <Navigate to="/dashboard" replace />;
  if (!checked) return <ScreenLoader />;

  // Step 1: consent. Once granted, fall through to profile-completion check.
  if (!hasConsent) return <ConsentScreen onConsented={() => setHasConsent(true)} />;

  // Step 2: profile completion (gender / medium / address / etc) AND the
  // contact details that outlive the college mailbox.
  //
  // Deliberately ONE gate, not two. A student who has filled neither sees a
  // single form; a student who finished their profile months ago sees the
  // same screen cut down to just the two missing questions. Adding a third
  // sequential pop-up would have been three screens between login and the
  // dashboard.
  if ((!profileCompleted || !contactCompleted) && locked) {
    return (
      <ProfileCompletionScreen
        locked={locked}
        /* when true, only personal email + LinkedIn are asked for */
        contactOnly={profileCompleted && !contactCompleted}
        prefill={prefill}
        onCompleted={() => { setProfileCompleted(true); setContactCompleted(true); }}
      />
    );
  }

  // 2027 passout students land on My Internships instead of the
  // Dashboard immediately after logging in — but only on that automatic
  // landing (location.state.postLogin, set by the login redirect and the
  // "/" root redirect). Clicking the "Dashboard" sidebar link afterward
  // has no such state, so it's not intercepted — Dashboard stays
  // reachable on demand, it just isn't where they land by default.
  if (
    locked?.graduation_year === 2027 &&
    location.pathname === '/dashboard' &&
    (location.state as { postLogin?: boolean } | null)?.postLogin
  ) {
    return <Navigate to="/my/internships" replace />;
  }

  return <>{children}</>;
};

// HOD + Admin guard for review/oversight pages (e.g. /approvals).
const ProtectedHODOrAdmin = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <ScreenLoader />;
  if (!isAuthenticated || !user) return <Navigate to="/login" replace />;
  if (user.role !== 'HOD' && user.role !== 'ADMIN')
    return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

// Admin-only guard (user management, system-wide settings).
const ProtectedAdmin = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <ScreenLoader />;
  if (!isAuthenticated || !user) return <Navigate to="/login" replace />;
  if (user.role !== 'ADMIN') return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

// Coordinator guard — COORDINATOR and ADMIN roles can access /cell/* routes.
// Admins are included so TPO/admin staff can view and manage drives.
// ALSO grants access to HODs (or any other role) flagged as dual-hat
// coordinators via User.is_internship_coordinator — matches the backend
// permission rule in apps/internship_cell/permissions.py.
const ProtectedCoordinator = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <ScreenLoader />;
  if (!isAuthenticated || !user) return <Navigate to="/login" replace />;
  const isCoordinator =
    user.role === 'COORDINATOR' ||
    user.role === 'ASSISTANT' ||
    user.role === 'ADMIN' ||
    user.is_internship_coordinator === true;
  if (!isCoordinator) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

// Admin + HOD guard — for the Cell overview page (TPO/HOD oversight).
const ProtectedAdminOrHOD = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <ScreenLoader />;
  if (!isAuthenticated || !user) return <Navigate to="/login" replace />;
  const canViewCellAdmin =
    user.role === 'ADMIN' || user.role === 'HOD' ||
    user.role === 'COORDINATOR' || user.role === 'ASSISTANT' ||
    user.is_internship_coordinator === true;
  if (!canViewCellAdmin)
    return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

// Insights guard — ADMIN + ASSISTANT + COORDINATOR (system-wide analytics).
// HOD has their own scoped /hod/insights page; this one is for staff.
const ProtectedInsights = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <ScreenLoader />;
  if (!isAuthenticated || !user) return <Navigate to="/login" replace />;
  const allowed =
    user.role === 'ADMIN' || user.role === 'ASSISTANT' || user.role === 'COORDINATOR';
  if (!allowed) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

// HOD + Cell staff guard — for student detail and HOD analytics.
// HODs see branch-scoped data; ADMIN, ASSISTANT, COORDINATOR see all students.
const ProtectedHODOrCellStaff = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <ScreenLoader />;
  if (!isAuthenticated || !user) return <Navigate to="/login" replace />;
  const allowed =
    user.role === 'HOD' || user.role === 'ADMIN' ||
    user.role === 'ASSISTANT' || user.role === 'COORDINATOR' ||
    user.is_internship_coordinator === true;
  if (!allowed) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

// Any authenticated user (settings page is available to all roles).
// Saves the current path in React Router location state so LoginRoute can
// redirect back here after a successful sign-in (e.g. shared drive links).
const ProtectedAny = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) return <ScreenLoader />;
  if (!isAuthenticated)
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  return <>{children}</>;
};

// ─────────────────────────────────────────────────────────────
// Google Identity Services typings (script loaded in index.html)
// ─────────────────────────────────────────────────────────────
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: {
              theme?: 'outline' | 'filled_blue' | 'filled_black';
              size?: 'large' | 'medium' | 'small';
              width?: number | string;
              text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
              shape?: 'rectangular' | 'pill' | 'circle' | 'square';
              logo_alignment?: 'left' | 'center';
            },
          ) => void;
          prompt: () => void;
        };
      };
    };
  }
}

// ─────────────────────────────────────────────────────────────
// Login page — Google Sign-In (primary) + email/password (fallback)
//
// Students sign in via Google using their @ljku.edu.in account.
// HODs and admins can also use Google, or expand the email/password
// form for demo accounts or manual credentials.
// ─────────────────────────────────────────────────────────────
const LoginRoute = () => {
  const { login, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const googleBtnRef = useRef<HTMLDivElement>(null);

  // Destination to return to after login (set by ProtectedAny when it redirects here).
  const from = (location.state as { from?: string } | null)?.from ?? '/dashboard';

  const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

  // Google Sign-In callback — sends the credential to our backend
  const handleGoogleCallback = async (response: { credential: string }) => {
    setGoogleLoading(true);
    try {
      // Same-origin relative URL by default. See comment in
      // src/api/axios.ts for why this isn't `http://localhost:8000`.
      const base = import.meta.env.VITE_API_BASE_URL || '';
      const apiUrl = `${base.replace(/\/$/, '')}/api/v1`;
      const { data } = await axios.post<{ access: string; refresh: string }>(
        `${apiUrl}/auth/google/`,
        { credential: response.credential },
        { headers: { 'Content-Type': 'application/json' }, timeout: 10000 },
      );
      login(data.access, data.refresh);
      toast.success('Signed in with Google.');
    } catch (err: unknown) {
      const responseData = axios.isAxiosError(err)
        ? (err.response?.data as { detail?: string; message?: string } | undefined)
        : undefined;
      const msg =
        responseData?.detail ||
        responseData?.message ||
        'Google sign-in failed. Please try again.';
      toast.error(msg);
    } finally {
      setGoogleLoading(false);
    }
  };

  // Initialize Google Identity Services button
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !googleBtnRef.current) return;

    const initGoogleBtn = () => {
      if (!window.google || !googleBtnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCallback,
      });
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: 'filled_blue',
        size: 'large',
        width: 320,
        text: 'signin_with',
        shape: 'pill',
        logo_alignment: 'left',
      });
    };

    // GIS script may still be loading (async defer)
    if (window.google) {
      initGoogleBtn();
    } else {
      const interval = setInterval(() => {
        if (window.google) {
          clearInterval(interval);
          initGoogleBtn();
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, [GOOGLE_CLIENT_ID]);

  // If the user is already authed and lands on /login, bounce.
  // Uses `from` so shared drive links survive the login redirect:
  //   student clicks /register/opening/<uuid> → redirected to /login
  //   → signs in → lands back on /register/opening/<uuid> (not /dashboard)
  // (Must be placed AFTER all hooks to satisfy React's Rules of Hooks.)
  if (!isLoading && isAuthenticated) {
    // postLogin marks this as the automatic just-signed-in landing (as
    // opposed to clicking the "Dashboard" sidebar link later) — see
    // ProtectedStudent's 2027-passout redirect, which only fires on this
    // flag so the student can still reach /dashboard on demand afterward.
    return <Navigate to={from} state={{ postLogin: true }} replace />;
  }

  // Email/password form submit (existing logic preserved)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Email and password are required.');
      return;
    }
    setSubmitting(true);
    try {
      // Same-origin relative URL by default. See comment in
      // src/api/axios.ts for why this isn't `http://localhost:8000`.
      const base = import.meta.env.VITE_API_BASE_URL || '';
      const apiUrl = `${base.replace(/\/$/, '')}/api/v1`;
      const { data } = await axios.post<{ access: string; refresh: string }>(
        `${apiUrl}/auth/token/`,
        { email, password },
        { headers: { 'Content-Type': 'application/json' }, timeout: 10000 },
      );
      login(data.access, data.refresh);
      toast.success('Signed in.');
    } catch (err: unknown) {
      const responseData = axios.isAxiosError(err)
        ? (err.response?.data as { detail?: string; message?: string } | undefined)
        : undefined;
      const msg =
        responseData?.detail ||
        responseData?.message ||
        'Invalid credentials or backend unreachable.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-surface-50 dark:bg-surface-950 px-4">
      <div className="p-8 md:p-10 glass rounded-3xl w-full max-w-sm space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-brand-500 to-purple-600 mb-2">
            Portal Career Flow
          </h1>
          <p className="text-surface-500 dark:text-surface-400 text-sm">
            Sign in with your university account
          </p>
        </div>

        {/* Google Sign-In (primary) */}
        {GOOGLE_CLIENT_ID ? (
          <div className="space-y-3">
            <div className="flex justify-center">
              <div
                ref={googleBtnRef}
                id="google-signin-btn"
                className="min-h-[44px] flex items-center justify-center"
              >
                {googleLoading && (
                  <div className="flex items-center gap-2 text-sm text-surface-500">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-surface-200 border-t-brand-500" />
                    Signing in…
                  </div>
                )}
              </div>
            </div>
            <p className="text-xs text-surface-400 dark:text-surface-500 text-center">
              Use your <span className="font-semibold">@ljku.edu.in</span> account
            </p>
          </div>
        ) : (
          <div className="rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 p-3 text-xs text-amber-800 dark:text-amber-200 text-center">
            Google Sign-In not configured.
            <br />
            Set <code className="font-mono bg-amber-100 dark:bg-amber-900/40 px-1 rounded">VITE_GOOGLE_CLIENT_ID</code> in your <code>.env</code>
          </div>
        )}

        {/* Divider */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-surface-200 dark:bg-surface-800" />
          <button
            type="button"
            onClick={() => setShowEmailForm(!showEmailForm)}
            className="text-xs text-surface-400 dark:text-surface-500 hover:text-surface-600 dark:hover:text-surface-300 transition-colors cursor-pointer select-none"
          >
            {showEmailForm ? 'Hide' : 'Sign in with email'}
          </button>
          <div className="flex-1 h-px bg-surface-200 dark:bg-surface-800" />
        </div>

        {/* Email/Password form (collapsible — for HOD/Admin or demo) */}
        {showEmailForm && (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label
                htmlFor="email"
                className="block text-xs font-semibold uppercase tracking-wider text-surface-500 dark:text-surface-400 mb-1"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl border border-surface-200 bg-white/70 px-3 py-2.5 text-sm text-surface-900 focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-surface-800 dark:bg-surface-900/70 dark:text-surface-50"
                placeholder="email@ljku.edu.in"
              />
            </div>
            <div>
              <label
                htmlFor="password"
                className="block text-xs font-semibold uppercase tracking-wider text-surface-500 dark:text-surface-400 mb-1"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border border-surface-200 bg-white/70 px-3 py-2.5 text-sm text-surface-900 focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-surface-800 dark:bg-surface-900/70 dark:text-surface-50"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white rounded-xl font-semibold transition-all shadow-md active:scale-95"
            >
              {submitting ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default function App() {
  return (
    <ThemeProvider>
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors closeButton />

          {/* AssistantProvider wraps everything so the Cmd+K palette
              and its history persist across route changes. The bottom-right
              launcher pill is mounted once at the bottom of this tree
              (see <AIAssistant /> below), and is auto-hidden for students. */}
          <AssistantProvider>
          <FilterProvider>
            <Suspense fallback={<ScreenLoader />}>
            <Routes>
              <Route path="/login" element={<LoginRoute />} />

              <Route
                path="/dashboard/*"
                element={
                  <ErrorBoundary>
                    <ProtectedDashboard />
                  </ErrorBoundary>
                }
              />

              {/* Student self-service */}
              <Route
                path="/my/internships"
                element={
                  <ErrorBoundary>
                    <ProtectedStudent>
                      <MyInternships />
                    </ProtectedStudent>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/my/drives"
                element={
                  <ErrorBoundary>
                    <ProtectedStudent>
                      <AvailableDrives />
                    </ProtectedStudent>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/my/achievements"
                element={
                  <ErrorBoundary>
                    <ProtectedStudent>
                      <Achievements />
                    </ProtectedStudent>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/my/freelancing"
                element={
                  <ErrorBoundary>
                    <ProtectedStudent>
                      <Freelancing />
                    </ProtectedStudent>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/my/hackathons"
                element={
                  <ErrorBoundary>
                    <ProtectedStudent>
                      <Hackathons />
                    </ProtectedStudent>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/my/other-events"
                element={
                  <ErrorBoundary>
                    <ProtectedStudent>
                      <OtherEvents />
                    </ProtectedStudent>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/my/documents"
                element={
                  <ErrorBoundary>
                    <ProtectedStudent>
                      <Documents />
                    </ProtectedStudent>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/my/announcements"
                element={
                  <ErrorBoundary>
                    <ProtectedStudent>
                      <Announcements />
                    </ProtectedStudent>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/my/activity"
                element={
                  <ErrorBoundary>
                    <ProtectedStudent>
                      <ActivityLogPage />
                    </ProtectedStudent>
                  </ErrorBoundary>
                }
              />

              {/* Oversight (HOD + Admin) */}
              <Route
                path="/approvals"
                element={
                  <ErrorBoundary>
                    <ProtectedHODOrAdmin>
                      <ApprovalsPage />
                    </ProtectedHODOrAdmin>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/approvals/history"
                element={
                  <ErrorBoundary>
                    <ProtectedHODOrAdmin>
                      <ApprovalHistoryPage />
                    </ProtectedHODOrAdmin>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/approvals/notes"
                element={
                  <ErrorBoundary>
                    <ProtectedHODOrAdmin>
                      <HODNotesPage />
                    </ProtectedHODOrAdmin>
                  </ErrorBoundary>
                }
              />

              {/* Admin-only */}
              <Route
                path="/users"
                element={
                  <ErrorBoundary>
                    <ProtectedAdmin>
                      <UserManagement />
                    </ProtectedAdmin>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/users/students/:enrollment"
                element={
                  <ErrorBoundary>
                    <ProtectedHODOrCellStaff>
                      <StudentDetail />
                    </ProtectedHODOrCellStaff>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/admin/imports"
                element={
                  <ErrorBoundary>
                    <ProtectedAdmin>
                      <ImportsPage />
                    </ProtectedAdmin>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/admin/announcements"
                element={
                  <ErrorBoundary>
                    <ProtectedAdmin>
                      <AnnouncementsPage />
                    </ProtectedAdmin>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/admin/hod-allocation"
                element={
                  <ErrorBoundary>
                    <ProtectedAdmin>
                      <HODAllocation />
                    </ProtectedAdmin>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/admin/insights"
                element={
                  <ErrorBoundary>
                    <ProtectedInsights>
                      <CohortInsights />
                    </ProtectedInsights>
                  </ErrorBoundary>
                }
              />

              {/* HOD Cohort Insights — scoped to the HoD's allocated students */}
              <Route
                path="/hod/insights"
                element={
                  <ErrorBoundary>
                    <ProtectedHODOrCellStaff>
                      <HODCohortInsights />
                    </ProtectedHODOrCellStaff>
                  </ErrorBoundary>
                }
              />

              {/* ── PUBLIC job description (no login) ──────────────────────
                  Deliberately NOT wrapped in ProtectedAny: this is the link
                  shared in WhatsApp and printed on the JD, so it must render
                  for a logged-out visitor. Registering is still gated — the
                  CTA sends them to /register/opening/:token, which IS
                  protected. ------------------------------------------------ */}
              <Route
                path="/r/:code"
                element={
                  <ErrorBoundary>
                    <PublicJDPage />
                  </ErrorBoundary>
                }
              />

              {/* ── Student self-registration (shareable drive links) ──────── */}
              <Route
                path="/register/opening/:openingId"
                element={
                  <ErrorBoundary>
                    <ProtectedAny>
                      <StudentRegistrationPage />
                    </ProtectedAny>
                  </ErrorBoundary>
                }
              />

              {/* ── Internship Cell (COORDINATOR + ADMIN) ──────────────────── */}
              <Route
                path="/cell/drives"
                element={
                  <ErrorBoundary>
                    <ProtectedCoordinator>
                      <DrivesPage />
                    </ProtectedCoordinator>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/cell/drives/:id"
                element={
                  <ErrorBoundary>
                    <ProtectedCoordinator>
                      <DriveDetailPage />
                    </ProtectedCoordinator>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/cell/drives/:id/bulk-mark"
                element={
                  <ErrorBoundary>
                    <ProtectedCoordinator>
                      <BulkMarkPage />
                    </ProtectedCoordinator>
                  </ErrorBoundary>
                }
              />

              {/* ── Cell admin overview (ADMIN + HOD) ────────────────────── */}
              <Route
                path="/cell/admin"
                element={
                  <ErrorBoundary>
                    <ProtectedAdminOrHOD>
                      <AdminOverviewPage />
                    </ProtectedAdminOrHOD>
                  </ErrorBoundary>
                }
              />



              {/* Any authenticated user */}
              <Route
                path="/settings"
                element={
                  <ErrorBoundary>
                    <ProtectedAny>
                      <SettingsPage />
                    </ProtectedAny>
                  </ErrorBoundary>
                }
              />

              <Route path="/" element={<Navigate to="/dashboard" state={{ postLogin: true }} replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
          </FilterProvider>

          {/* In-portal AI assistant — pill launcher + Cmd+K palette.
              Renders nothing for unauthenticated visitors or students. */}
          <AIAssistant />
          </AssistantProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
    </ThemeProvider>
  );
}
